import { LegalPlaceholder } from "@/components/layout/LegalPlaceholder";

/**
 * /refunds - Whistll Refund Policy (last updated April 2026).
 * Copy transcribed from ~/Documents/Whistll/whistll-all-documents/
 * whistll-refund-policy.pdf. Keep in sync with both the PDF and the
 * mobile app's mirror in apps/mobile/src/lib/legal/content.ts.
 */
export function RefundPolicyPage() {
  return (
    <LegalPlaceholder title="Refund Policy" lastUpdated="April 2026">
      <h2>1. Overview</h2>
      <p>
        This Refund Policy explains how and when refunds are issued on
        Whistll. It applies to all users across the United Kingdom, United
        States, Canada, Australia, and New Zealand. Where local consumer
        protection laws provide stronger rights, those rights apply in
        addition to this Policy.
      </p>
      <p>
        This Policy forms part of, and should be read alongside, the
        Whistll User Terms and Conditions.
      </p>

      <h2>2. How payments work</h2>
      <p>
        When you join a paid game on Whistll, your card is{" "}
        <strong>pre-authorised</strong> for your share of the court fee
        plus the Whistll platform fee. You are not charged unless and
        until the game is confirmed.
      </p>
      <p>
        A game is confirmed only when the required number of players for
        that format is reached. If the required number is not reached by
        one (1) hour before tip-off, the game is automatically cancelled
        and the pre-authorisation is released in full. You will not be
        charged.
      </p>

      <h2>3. Automatic refunds - no action needed</h2>
      <p>
        Refunds are automatic in the following situations. You do not need
        to request them.
      </p>
      <p>
        <strong>Game auto-cancelled (threshold not met):</strong> If a
        game fails to hit the required player count by 1 hour before
        tip-off, your pre-authorisation is released in full. No charge is
        made.
      </p>
      <p>
        <strong>Host cancels before the 24-hour cut-off:</strong> If the
        host cancels a confirmed game more than 24 hours before tip-off,
        you receive a full refund of both the court fee and the Whistll
        platform fee.
      </p>
      <p>
        <strong>
          You cancel your participation before the 24-hour cut-off:
        </strong>{" "}
        If you withdraw from a confirmed game more than 24 hours before
        tip-off, you receive a full refund of both the court fee and the
        Whistll platform fee. Your spot opens back up for another player.
      </p>

      <h2>4. No refunds in these situations</h2>
      <p>You are not eligible for an automatic refund in the following situations:</p>
      <ul>
        <li>
          <strong>You cancel within 24 hours of tip-off</strong> - your
          spot is confirmed and no refund is automatic.
        </li>
        <li>
          <strong>The host cancels within 24 hours of tip-off</strong> -
          refunds depend on the host's reason; extenuating circumstances
          may apply (see section 5).
        </li>
        <li>
          <strong>You do not attend a confirmed game</strong> - no-shows
          are not refunded.
        </li>
        <li>
          <strong>You attend but believe the game was not as expected</strong>{" "}
          - disputes over game quality should be raised through in-app
          reporting, not chargebacks.
        </li>
      </ul>

      <h2>5. Extenuating circumstances</h2>
      <p>
        If something genuinely unforeseen stops you from using a booking,
        you may submit a claim to Whistll at{" "}
        <a href="mailto:hello@whistll.com">hello@whistll.com</a>.
      </p>
      <p>Your claim must:</p>
      <ul>
        <li>
          be submitted as soon as reasonably practicable and no later than
          14 days after the booking date;
        </li>
        <li>include a clear description of the circumstances; and</li>
        <li>include any supporting evidence where reasonably available.</li>
      </ul>
      <p>
        Whistll will review all claims at its sole discretion. Where we
        determine that extenuating circumstances apply, we may offer:
      </p>
      <ul>
        <li>a full or partial refund to your original payment method;</li>
        <li>Whistll App credits applicable to future bookings; or</li>
        <li>another remedy we consider fair and reasonable.</li>
      </ul>
      <p>Examples of circumstances that may be considered include:</p>
      <ul>
        <li>Serious illness or injury</li>
        <li>Bereavement</li>
        <li>Venue failure or closure</li>
        <li>Significant technical errors caused by Whistll</li>
        <li>Other events genuinely beyond your reasonable control</li>
      </ul>
      <p>
        We aim to respond to extenuating circumstances claims within 10
        working days.
      </p>
      <p>
        <strong>Important:</strong> Extenuating circumstances claims are
        considered at Whistll's discretion. A claim does not automatically
        entitle you to a refund. Each case is assessed on its own merits.
      </p>

      <h2>6. How to request a refund</h2>
      <p>
        <strong>Automatic refunds:</strong> No action needed. The refund
        is processed automatically when eligible.
      </p>
      <p>
        <strong>Extenuating circumstances claims:</strong> Email{" "}
        <a href="mailto:hello@whistll.com">hello@whistll.com</a> with:
      </p>
      <ul>
        <li>Your name and the email address on your account</li>
        <li>The game date, time, and venue</li>
        <li>A description of the circumstances</li>
        <li>Any supporting documents</li>
      </ul>

      <h2>7. Refund timing</h2>
      <p>Refunds processed through Stripe typically take:</p>
      <ul>
        <li>5–10 business days to appear on credit card statements</li>
        <li>Up to 3 business days for debit cards</li>
      </ul>
      <p>The exact timing depends on your card issuer.</p>

      <h2>8. Payment disputes and chargebacks</h2>
      <p>
        If you believe a charge is incorrect or unauthorised, please
        contact Whistll first at{" "}
        <a href="mailto:hello@whistll.com">hello@whistll.com</a> before
        raising a chargeback with your bank or card issuer. We aim to
        respond to payment queries within 3 working days.
      </p>
      <p>
        Raising a chargeback without contacting Whistll first, or for
        reasons Whistll reasonably considers unjustified, may result in:
      </p>
      <ul>
        <li>Investigation and contesting of the chargeback</li>
        <li>Recovery of chargeback fees and administrative costs</li>
        <li>Suspension of your Whistll account</li>
      </ul>
      <p>
        This does not affect your statutory consumer rights where a
        service has not been delivered or is materially defective.
      </p>

      <h2>9. Venue refunds</h2>
      <p>
        Whistll is a platform connecting players with third-party venue
        providers. Where a venue cancels or fails to deliver the booking,
        you are entitled to a full refund processed by Whistll, who will
        then recover the amount from the venue through Stripe Connect's
        clawback mechanism.
      </p>
      <p>
        Disputes about the quality of the venue, facility condition, or
        venue conduct are covered in the User Terms and Conditions.
      </p>

      <h2>10. Country-specific consumer rights</h2>
      <p>This Policy does not limit your statutory consumer rights.</p>
      <p>
        <strong>United Kingdom</strong> - nothing in this Policy affects
        your rights under the Consumer Rights Act 2015.
      </p>
      <p>
        <strong>United States</strong> - nothing in this Policy affects
        your rights under applicable federal, state, or local consumer
        protection laws.
      </p>
      <p>
        <strong>Canada</strong> - nothing in this Policy affects your
        rights under the Consumer Protection Act of your province.
      </p>
      <p>
        <strong>Australia</strong> - nothing in this Policy affects your
        rights under the Australian Consumer Law.
      </p>
      <p>
        <strong>New Zealand</strong> - nothing in this Policy affects your
        rights under the Consumer Guarantees Act 1993 or the Fair Trading
        Act 1986.
      </p>

      <h2>11. Changes to this Policy</h2>
      <p>
        Whistll may update this Policy from time to time. Material changes
        will be notified to users via the App or by email. The "Last
        updated" date at the top reflects the most recent version.
      </p>

      <h2>12. Contact us</h2>
      <p>For all refund-related queries, please contact:</p>
      <p>
        <strong>Email:</strong>{" "}
        <a href="mailto:hello@whistll.com">hello@whistll.com</a>
        <br />
        <strong>Company number:</strong> 17172530
      </p>
      <p>We aim to respond within 3 working days.</p>
    </LegalPlaceholder>
  );
}
